import { Box, Grid } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../../api/Api';
import { ListCommunityPostsResponse } from '../../../api/communityApi';
import { useRequest } from '../../../api/Request';
import { CommunityPost } from '../../../database/community';
import LoadingPage from '../../../loading/LoadingPage';
import LoadMoreButton from '../LoadMoreButton';
import CommunityPostCard from './PostCard';
import { useCache } from '../../../api/cache/Cache';
import { useAuth } from '../../../auth/Auth';
import { GetBatchUsersResponse } from '../../../database/user';
import CommunityPostModal from './PostModal';

interface CommunityPostTabProps {
    query: string;
    data?: ListCommunityPostsResponse;
    setData: (data: ListCommunityPostsResponse) => void;
    category: string;
    /** Post ID in format "owner_createdAt" to deep-link to */
    linkedPostId?: string;
    /** Called when the deep-linked post modal is closed */
    onClearLinkedPost?: () => void;
}
const CommunityPostTab: React.FC<CommunityPostTabProps> = ({
    query,
    data,
    setData,
    category,
    linkedPostId,
    onClearLinkedPost,
}) => {
    const getPostsRequest = useRequest<ListCommunityPostsResponse>();
    const getLinkedPostRequest = useRequest<CommunityPost>();
    const getUsersBatchRequest = useRequest<GetBatchUsersResponse>();
    const api = useApi();
    const cache = useCache();
    const auth = useAuth();
    const user = auth.user;

    const [linkedPost, setLinkedPost] = useState<CommunityPost | null>(null);
    const linkedCardRef = useRef<HTMLDivElement | null>(null);

    const onSave = (i: number, post: CommunityPost) => {
        const newData = data?.posts || [];
        setData({
            posts: [...newData.slice(0, i), post, ...newData.slice(i + 1)],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    const onDelete = (i: number) => {
        const newData = data?.posts || [];
        setData({
            posts: [...newData.slice(0, i), ...newData.slice(i + 1)],
            lastEvaluatedKey: data?.lastEvaluatedKey || '',
        });
    };

    const cacheUsersFromPosts = useCallback(
        (posts: CommunityPost[]) => {
            const uniqueUsernames = Array.from(
                new Set(
                    posts.flatMap((post) => {
                        const usernames = [post.owner];
                        if (post.comments && post.comments.length > 0) {
                            const lastComment = post.comments[post.comments.length - 1];
                            if (lastComment.owner) {
                                usernames.push(lastComment.owner);
                            }
                        }
                        return usernames;
                    }),
                ),
            );

            const missingUsernames = uniqueUsernames.filter(
                (username) => !cache.userSummaries.get(username),
            );

            if (missingUsernames.length > 0) {
                getUsersBatchRequest.onStart();
                api.getUsersBatch(missingUsernames)
                    .then((getUsersBatchResponse) => {
                        const fetchedUsers = getUsersBatchResponse.data.users;
                        cache.userSummaries.putMany(fetchedUsers);
                        getUsersBatchRequest.onSuccess();
                    })
                    .catch((err) => {
                        console.error('Fetch users for requests error: ', err);
                        getUsersBatchRequest.onFailure(err);
                    });
            }
        },
        [api, cache.userSummaries, getUsersBatchRequest],
    );

    const handleResponse = useCallback(
        (resp: ListCommunityPostsResponse) => {
            // setLastStartKey(data?.lastEvaluatedKey || '');

            // const seen: Record<string, boolean> = {};
            const newEntries = resp.posts.sort((lhs, rhs) =>
                rhs.createdAt.localeCompare(lhs.createdAt),
            );

            cacheUsersFromPosts(newEntries);

            setData({
                posts: newEntries,
                lastEvaluatedKey: resp.lastEvaluatedKey,
            });
        },
        [setData, cacheUsersFromPosts],
    );

    const handleResponseLoadMore = useCallback(
        (resp: ListCommunityPostsResponse) => {
            const newEntries = (data?.posts || []).concat(
                resp.posts.sort((lhs, rhs) => rhs.createdAt.localeCompare(lhs.createdAt)),
            );

            cacheUsersFromPosts(newEntries);

            setData({
                posts: newEntries,
                lastEvaluatedKey: resp.lastEvaluatedKey,
            });
        },
        [data, setData, cacheUsersFromPosts],
    );

    useEffect(() => {
        if (user && !cache.userSummaries.get(user.username)) {
            cache.userSummaries.put(user);
        }

        if (!getPostsRequest.isSent()) {
            getPostsRequest.onStart();
            if (query === 'recent') {
                api.listPostsByCategory(category)
                    .then((resp) => {
                        handleResponse(resp.data);
                        getPostsRequest.onSuccess(resp.data);
                    })
                    .catch((err) => {
                        console.error('listCommunityPosts: ', err);
                        getPostsRequest.onFailure(err);
                    });
            } else if (query === 'my') {
                api.listCommunityPostsByOwner(category)
                    .then((resp) => {
                        handleResponse(resp.data);
                        getPostsRequest.onSuccess(resp.data);
                    })
                    .catch((err) => {
                        console.error('listCommunityPosts: ', err);
                        getPostsRequest.onFailure(err);
                    });
            }
        }
    }, [
        getPostsRequest,
        api,
        handleResponse,
        query,
        cache.userSummaries,
        user,
        category,
    ]);

    // Deep-link: fetch the specific post if it's not already in the loaded data
    useEffect(() => {
        if (!linkedPostId) {
            setLinkedPost(null);
            return;
        }

        const separatorIndex = linkedPostId.indexOf('~');
        if (separatorIndex === -1) return;

        const owner = linkedPostId.substring(0, separatorIndex);
        const createdAt = linkedPostId.substring(separatorIndex + 1);

        // Check if the post is already in the loaded list
        const existing = data?.posts.find(
            (p) => p.owner === owner && p.createdAt === createdAt,
        );
        if (existing) {
            setLinkedPost(existing);
            return;
        }

        // Fetch the post from the API
        if (!getLinkedPostRequest.isSent()) {
            getLinkedPostRequest.onStart();
            api.getPost(owner, createdAt)
                .then((resp) => {
                    setLinkedPost(resp.data);
                    cacheUsersFromPosts([resp.data]);
                    getLinkedPostRequest.onSuccess(resp.data);
                })
                .catch((err) => {
                    console.error('getPost for deep-link: ', err);
                    getLinkedPostRequest.onFailure(err);
                });
        }
    }, [linkedPostId, data, api, getLinkedPostRequest, cacheUsersFromPosts]);

    // Scroll to the linked post card when it appears in the grid
    useEffect(() => {
        if (linkedPostId && linkedCardRef.current) {
            linkedCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [linkedPostId, data]);

    const handleLinkedPostSave = (post: CommunityPost) => {
        setLinkedPost(post);
        // Also update in the grid data if present
        const idx = data?.posts.findIndex(
            (p) => p.owner === post.owner && p.createdAt === post.createdAt,
        );
        if (idx !== undefined && idx >= 0) {
            onSave(idx, post);
        }
    };

    const onLoadMore = () => {
        getPostsRequest.onStart();
        if (query === 'recent') {
            api.listPostsByCategory(category, data?.lastEvaluatedKey || '')
                .then((resp) => {
                    handleResponseLoadMore(resp.data);
                    getPostsRequest.onSuccess(resp.data);
                })
                .catch((err) => {
                    console.error('listCommunityPosts: ', err);
                    getPostsRequest.onFailure(err);
                });
        } else if (query === 'my') {
            api.listCommunityPostsByOwner(category, data?.lastEvaluatedKey || '')
                .then((resp) => {
                    handleResponseLoadMore(resp.data);
                    getPostsRequest.onSuccess(resp.data);
                })
                .catch((err) => {
                    console.error('listCommunityPosts: ', err);
                    getPostsRequest.onFailure(err);
                });
        }
    };

    if (!getPostsRequest.isSent() || (getPostsRequest.isLoading() && !data)) {
        return <LoadingPage />;
    }

    const isPostLinked = (post: CommunityPost) =>
        linkedPostId === `${post.owner}~${post.createdAt}`;

    // If the linked post was fetched separately (not in the grid), show a standalone modal
    const linkedPostNotInGrid =
        linkedPost &&
        linkedPostId &&
        !data?.posts.some(
            (p) => p.owner === linkedPost.owner && p.createdAt === linkedPost.createdAt,
        );

    return (
        <>
            {linkedPostNotInGrid && (
                <CommunityPostModal
                    post={linkedPost}
                    isModalOpen={true}
                    handleCloseModal={() => onClearLinkedPost?.()}
                    onSave={handleLinkedPostSave}
                />
            )}
            <Grid container>
                {data?.posts.map((post, i) => {
                    const isLinked = isPostLinked(post);
                    return (
                        <Grid
                            key={post.owner + post.createdAt}
                            item
                            xs={12}
                            sm={6}
                            md={6}
                            padding={2}
                        >
                            <Box ref={isLinked ? linkedCardRef : undefined}>
                                <CommunityPostCard
                                    post={post}
                                    onSave={(e) => onSave(i, e)}
                                    onDelete={() => onDelete(i)}
                                    initialOpen={isLinked}
                                    onModalClose={onClearLinkedPost}
                                />
                            </Box>
                        </Grid>
                    );
                })}
            </Grid>
            <LoadMoreButton
                request={getPostsRequest}
                startKey={data?.lastEvaluatedKey || ''}
                onLoadMore={onLoadMore}
            />
        </>
    );
};
export default CommunityPostTab;
